import { VIEWER_ERROR_CODES, type ViewerErrorCode } from './api.js';

/**
 * 表示文言。
 *
 * **v1 は `ja` のみ実装する**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * `data-signa-lang` は将来の拡張点として口だけ開けており、未知の値は `ja` に落ちる。
 */

export type Lang = 'ja';

export const MESSAGES = {
  ja: {
    loading: '読み込み中…',
    // **画像も受け付けるようになった**(#158)。案内も素材で言う。
    dropHint: '動画・画像をここにドロップ',
    /**
     * **盤面の中に出す案内**(#208)。**素材が入っていないセルだけ。**
     *
     * [`dropHint`](#dropHint) と役割が違う —— あちらは**ドラッグ中**に
     * 「落とせる」と示すもので、**ドラッグを始めた人にしか見えない。**
     * こちらは**何もしていない状態**で「ここに入れられる」と伝える。
     *
     * **短くする。** 枠の中に 1 行で収めたいが、分割した盤面ではセルが狭い。
     * **収まらないセルにも文言は出る**(#261。枠からはみ出す)—— 長いほど
     * はみ出す文言どうしが重なりやすく、重なる分は描画コアが間引く(`placeHints`)。
     *
     * **素材の種類を言わない**(#239)。動画も画像も入る(#158)—— 片方だけ
     * 名乗ると、もう片方を落とせないと読める。
     *
     * **短くしても「収まる」扱いのセルは広がらない**(#239)。描画コアは
     * [`emptyCellTouch`](#emptyCellTouch) と**長いほうで寸法を見積もる**
     * (`outlines.ts` の `longestHintText`。どちらが出ても同じ判定にするため)。
     * いまは**タッチ向けの 8 文字が上限を握っていて、実効は 120px** ——
     * **ここを 7 文字より短くしても変わらない。**
     */
    emptyCellDrop: 'ここにドロップ',
    /**
     * **素材の種別を指定した枠の案内**(#240 / [ADR-0038](../../../../docs/adr/0038-frame-source-kind-hint.md))。
     *
     * **「ここに」を付けない。** どちらも 7 文字に収めてあり、
     * [`emptyCellTouch`](#emptyCellTouch)(8 文字)が上限を握ったままになる ——
     * **種別を指定しても「収まる」扱いのセルの広さが変わらない。**
     * 「ここに動画をドロップ」(10 文字 = 146px)にすると、**120〜145px のセルが
     * 「収まらない」扱いになり、重なったときに間引かれる側に回る**
     * (#261 より前は、そのセルで案内が消えていた)。
     */
    emptyCellDropVideo: '動画をドロップ',
    /** [`emptyCellDropVideo`](#emptyCellDropVideo) の画像版。**同じく 7 文字。** */
    emptyCellDropImage: '画像をドロップ',
    /**
     * 同じ場所に出す、**タッチ端末向け**の案内(#208)。
     *
     * **タッチ端末では D&D で素材を入れられない**(#159 で「素材を選ぶ」
     * ボタンを廃止した)。「ここにドロップ」をそのまま出すと**できない操作を
     * 案内する**ことになるので、**別の端末で試せる**ことを伝える。
     *
     * 出し分けは CSS(`(hover: none) and (pointer: coarse)`)—— JS で判定して
     * 保持すると、**タッチ対応のノート PC を誤判定**する。
     */
    emptyCellTouch: 'PC で試せます',
    // **素材を当てるまで出す**(#159 で「常時表示」から変更)。
    // すべて解除したら戻す —— 消えたままだと「送信されたのでは」と読める。
    // **担保は表示ではなく、送信のコードが存在しないこと**(設計書 9.5)。
    noUpload: '素材はサーバに送信されません',
    play: '再生',
    pause: '停止',
    // **「同期」は先頭合わせ**(#123 と同じ定義)。押すと全部を 0 秒から
    // 流し直し、そのあとも定期的にずれを見張る。
    sync: '同期',
    // **静止画の書き出し**(#180 / ADR-0036)。**動画が 1 本も無く、画像だけのとき**に出す
    // (同意書 Q2)—— 動く要素が無いので、動画の代わりに「今の見え方」を 1 枚で渡せるようにする。
    saveImage: '画像を保存',
    // **書き出せなかった。** 理由を問わず**黙って壊れない**ための受け皿(ADR-0036 決定 8)。
    exportFailed: '書き出せませんでした',
    // **動画の書き出し**(#181)。動画が 1 本でもあれば出す。
    exportVideo: '動画を書き出す',
    /** 進捗。`{n}` を百分率に置き換える。 */
    exporting: '書き出し中… {n}%',
    exportCancel: '中止',
    /**
     * 書き出し中の案内(#233)。
     *
     * **操作を止めていることを伝える。** 何も言わずに押せなくすると「壊れた」と読める。
     */
    exportBusy: '書き出しが終わるまで操作できません',
    /**
     * **タブを切り替えさせない**(#354 / 同意書 Q1)。
     *
     * 書き出しは画面の更新(`requestAnimationFrame`)で進むので、**隠れたタブでは進まない** ——
     * 数十秒の待ちで別のタブへ移ると、戻るまで止まったままになる。
     * 裏で続ける仕組みは持たないので、**先に伝えて防ぐ。**
     */
    exportKeepVisible:
      'このタブを表示したままにしてください(切り替えている間は書き出しが進みません)',
    /**
     * **足りていないときの確認**(同意書)。**押させないのではなく尋ねる** ——
     * 設置イメージの確認では「1 枠だけ入れて見る」が普通にある。
     *
     * **数えているのは素材全般**(`assignments.size`)。動画に限らない(#239)。
     */
    exportPartial: '素材が入っていない枠があります。このまま書き出しますか?',
    /**
     * **利用上の注意**(#259)。動画の書き出しと静止画の保存の前に**毎回**出す。
     *
     * **改行は表示に残る**(`white-space: pre-line`)。
     */
    exportNotice:
      '※画像のアップロード、プレビューおよびダウンロードは、お客様ご自身の責任においてご利用ください。\n' +
      '第三者の著作権・肖像権その他の権利を侵害する画像の使用はお控えください。',
    noticeCancel: 'キャンセル',
    noticeConfirmExport: '書き出す',
    noticeConfirmSave: '保存する',
    // **切ったことは伝える**(ADR-0036 決定 6 の上限 60 秒)。
    exportTruncated: '長い素材は 60 秒までで切りました',
    // **黙って壊れない**(ADR-0036 決定 8)。WebCodecs が無い / コーデックが通らない環境。
    exportUnsupported: 'この環境では動画を書き出せません',
    // **「同期」の失敗ではない。** 自動再生の拒否と「再生」の失敗の両方で使う。
    playFailed: '再生を開始できませんでした',
    retry: '再試行',
    notAvailable: 'このプレビューは現在ご利用いただけません',
    // **理由を伝える**(#229 / ADR-0041)。「利用できません」だけでは客先が
    // 何をすればよいか分からない —— **共有元に頼めば再発行できる**と分かる形にする。
    linkExpired: 'この共有リンクは有効期限が切れています。共有元にお問い合わせください',
    linkRevoked: 'この共有リンクは無効になっています。共有元にお問い合わせください',
    /**
     * **共有フォルダに表示できる盤面が無い**(#267)。登録が無い、または全部が公開 OFF・ゴミ箱。
     *
     * **「利用できません」と分ける。** リンクは生きている(ADR-0046 の決定 4)——
     * 盤面を戻せばそのまま見えるようになる。フォルダ名と一緒に出す。
     */
    emptyFolder: '表示できる盤面がありません',
    // **閲覧者に「登録して」とは言わない**(埋め込み先の管理者の仕事)。
    // 何を直すかはコンソールに出す(api.ts)。
    originNotAllowed: 'このサイトではプレビューを表示できません',
    network: '読み込めませんでした',
    unsupported: 'この環境では動画の合成表示に対応していません',
    /**
     * **直し方まで伝える**(#341)。HEVC(iPhone の標準の設定)はブラウザによって再生できない ——
     * 「形式が違う」だけでは何をすればよいか分からない。**S04 の文言と一字一句そろえる**
     * (`packages/web/src/pages/editor/TestVideoPanel.tsx`)。
     */
    videoUnsupported: 'この動画形式は再生できません。H.264 の MP4 で書き出した動画をお使いください',
    // **画像には「再生」と言わない。** 直しようのない案内にしない(#158)。
    imageUnsupported: 'この画像形式は表示できません',
    // **上限の数値は呼び出し側が添える**(環境によって違うため)。
    tooLarge: '素材が大きすぎて表示できません',
    /** 上限の数値を添える(環境によって違う)。`{n}` を置き換える。 */
    tooLargeLimit: '素材が大きすぎて表示できません(一辺 {n}px まで)',
    renderFailed: '表示できませんでした',
    // **動画と画像の 2 種を受け付ける**(#158)。SVG は対象外(ADR-0030)。
    notSupported: '動画または画像のファイルを選んでください',
    chooseFrame: '素材を入れる枠を選んでください',
    /**
     * 共有していた素材が待っている間に外された(#168)。
     *
     * **「失敗」とは言わない。** 利用者の操作が速すぎただけで、
     * もう一度入れれば通る。
     */
    retryAssign: '素材の割り当てが変わりました。もう一度入れてください',
  },
} as const satisfies Record<Lang, Record<string, string>>;

/** 未知の言語指定は `ja` に落とす。 */
export function resolveLang(raw: string | null | undefined): Lang {
  return raw === 'ja' ? 'ja' : 'ja';
}

export function messages(lang: Lang = 'ja'): (typeof MESSAGES)['ja'] {
  return MESSAGES[lang];
}

/**
 * エラーコードに対応する表示文言。
 *
 * **`INVALID_TOKEN` は何も表示しない**(埋め込みミスであり、閲覧者に
 * 見せる意味がない — #33)。`BACKGROUND_PENDING` はローディング継続。
 */
export function errorMessage(code: ViewerErrorCode, lang: Lang = 'ja'): string | null {
  const m = messages(lang);
  switch (code) {
    case VIEWER_ERROR_CODES.NOT_AVAILABLE:
      return m.notAvailable;
    case VIEWER_ERROR_CODES.LINK_EXPIRED:
      return m.linkExpired;
    case VIEWER_ERROR_CODES.LINK_REVOKED:
      return m.linkRevoked;
    case VIEWER_ERROR_CODES.NETWORK:
      return m.network;
    case VIEWER_ERROR_CODES.ORIGIN_NOT_ALLOWED:
      return m.originNotAllowed;
    case VIEWER_ERROR_CODES.UNSUPPORTED:
      return m.unsupported;
    case VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED:
      return m.videoUnsupported;
    case VIEWER_ERROR_CODES.RENDER_FAILED:
      return m.renderFailed;
    case VIEWER_ERROR_CODES.INVALID_TOKEN:
    case VIEWER_ERROR_CODES.BACKGROUND_PENDING:
      return null;
  }
}

/** そのエラーで手動の再試行を出すか。 */
export function isRetryable(code: ViewerErrorCode): boolean {
  return code === VIEWER_ERROR_CODES.NETWORK;
}
